import dayjs from '@dayjs';
import {
  i18n,
} from '@i18n';
import {
  isNullish,
} from '@qmulus/ui/utils/common.ts';
import * as Sentry from '@sentry/vue';
import {
  AxiosResponse,
} from 'axios';
import {
  ZodDefault,
  ZodNumber,
} from 'zod';

import {
  isPlainObject,
} from './common';
import {
  formatDayJsDateWithoutTime,
  formatDayjsDate,
  formatFromDuration,
} from './date-formats';
import {
  roundTo,
} from './number';
import {
  TNumericLimitsProps,
  getZodLimitsForNumberInputProps,
  getZodNumberLimits,
} from './zod';

import {
  components,
} from '@/generated/pt9800/openapi';
import materialZod from '@/generated/pt9800/zod/material';
import {
  components as componentsQmulus,
} from '@/generated/qmulus/openapi';
import {
  attributeUnitMap,
} from '@/types/attribute-type';

// empirically assumed number of MS, which takes PT9800 to apply changes
export const PT9800_SHOPLAYOUT_TIMEOUT_MS = 5000;

/**
 * Some pt9800 number fields carry no fraction-digit constraint in their zod schema
 * (no `multipleOf`), yet must still render with a fixed number of decimals. This map is the
 * single source of truth for that fallback precision, keyed by the schema field name so the
 * same field renders identically everywhere it is shown.
 */
export const PT9800_FIELD_DEFAULT_FRACTION_DIGITS = {
  processDepth: 2,
  processDepthAt: 2,
  targetSurfC: 2,
  co: 2,
  h2: 2,
} as const;

export type TPt9800FractionDigitsField = keyof typeof PT9800_FIELD_DEFAULT_FRACTION_DIGITS;

/**
 * Build InputNumber limit props from a zod schema, falling back to the field's default
 * fraction digits (see `PT9800_FIELD_DEFAULT_FRACTION_DIGITS`) when the schema defines none.
 */
export function getPt9800FieldNumberInputProps(
  schema: ZodNumber | ZodDefault<ZodNumber>,
  field: TPt9800FractionDigitsField,
): TNumericLimitsProps {
  const limitsProps = getZodLimitsForNumberInputProps(schema);
  const defaultFractionDigits = PT9800_FIELD_DEFAULT_FRACTION_DIGITS[field];
  return {
    ...limitsProps,
    maxFractionDigits: limitsProps.maxFractionDigits ?? defaultFractionDigits,
    minFractionDigits: limitsProps.minFractionDigits ?? defaultFractionDigits,
  };
}

enum PT9800TemplateTokenType {
  I18N = 'I18N',
  DATE = 'DATE',
  DATETIME = 'DATETIME',
  DURATION = 'DURATION',
}

// A recipe group is exposed as one menu item that carries the group code in `programPrefix` and
// the furnace workplace in `programArg` (both nullable). Batch furnaces use a `RECIPES` item, belt
// furnaces a `RECIPES_BELT` item; both are recipe-template groups. (Editing a *running* belt recipe
// is gated elsewhere — by the belt furnace view — not by these template items.)
// pt98 rights are defined per configured application, not per recipe, so the same `programPrefix`
// can appear on more than one application (a real, if misconfigured, case on dev PE 1123). Match
// from most to least specific: prefer the app whose `programArg` is this workplace, else — per the
// agreed contract — just take the first application found for the prefix.
type TRecipePermission = components['schemas']['UserMenuItemPrivileges'] | undefined;
export function getUserPermissionForRecipe(menu: components['schemas']['UserMenu'] | undefined, recipePrefix: string, workplaceId: number): TRecipePermission {
  const recipeGroupItems = menu?.groups
    ?.flatMap((group) => group.items ?? [])
    .filter((item) => (item.type === 'RECIPES' || item.type === 'RECIPES_BELT')
      && item.programPrefix === recipePrefix);

  if (isNullish(recipeGroupItems) || recipeGroupItems.length === 0) {
    return undefined;
  }

  const workplaceMatch = recipeGroupItems.find((item) => item.programArg === workplaceId);
  return (workplaceMatch ?? recipeGroupItems[0]).rights;
}

export function getShopLayoutTooltip(list?: Record<string, any>[]): string | undefined {
  return list
    ? list
      .map((_object) => Object.entries(_object)
        .map(([
          key,
          value,
        ]) => `${key}: ${value}`))
      .flat()
      .join('\n')
    : undefined;
}

export const processFlowEntryColumnTypeToValueMap: Record<
  components['schemas']['processFlowEntryColumn']['type'],
  keyof components['schemas']['processFlowEntry']
  | keyof components['schemas']['processFlowStepRuntimeInfo']
  | undefined
> = {
  // processFlow
  ACKNOLEDGE_ERP: 'acknowledgeErp',
  AUTOSKIP: 'autoSkip',
  SKF_MODE_COMBINED_STEP_FROM: 'combinedStepFrom',
  SKF_MODE_COMBINED_STEP_TO: 'combinedStepTo',
  COMMENT: 'comment',
  SKF_MODE_DROPPING_TIME_SP: 'droppingTime',
  MANUAL_START_STOP: 'manualStartStop',
  SKF_MODE_MAXIMUM_TIME: 'maximumTime',
  SKF_MODE_MINIMUM_TIME: 'minimumTime',
  RANZIEHEN: 'moveStepCloseToNextStep',
  OPERATION: 'operationName',
  FRAESEN_WITTEN_OR_QM_RELEVANT_STEP: 'qmRelevantStep',
  RECIPE: 'recipe',
  PLAN_TIME: 'planTimeInMin',
  SKF_MODE_PLAN_WISH_TIME: 'planWishTime',
  STEP_NO: 'pos',
  STATISTIC_RELEVANT_STEP: 'statisticRelevantStep',
  SKF_MODE_TRANSFER_TIME_PLAN: 'transferTimePlan',
  SKF_MODE_TRANFER_TIME_SP: 'transferTimeSP',
  TIME_STAY: 'waitTimeInMin',
  WORKPLACE: 'wpName',

  // no direct mapping
  OPERATION_LIST: undefined,
  WORKPLACE_LIST: undefined,
  RECIPE_LIST: undefined,

  // runtime
  RUNTIME_TIME_START: 'startTime',
  RUNTIME_TIME_STOP: 'endTime',
  RUNTIME_WORKPLACE_PLANNED: 'plannedWpName',
  RUNTIME_STATUS: 'status',
  RUNTIME_CHECKED_INFO: 'checked',
  RUNTIME_TRANSFER_TIME: 'transferTimeInSec',
  RUNTIME_SOAK_TIME: 'soakTime',
  RUNTIME_TEMP_AFTER_PLANNING: 'tempAfterPlanning',
  RUNTIME_PREPARE_START_TIME: 'prepareStartTime',
  RUNTIME_DYN_SETPOINT: 'dynSetpoint',
};

export const partColumnTypeToValueMap: Record<components['schemas']['processFlowContentColumn']['type'], keyof components['schemas']['processFlowPartData'] | undefined> = {
  STEP_NO: 'pos',
  PART_NO: 'partName',
  PART_TYPE: 'partDescription',
  AMOUNT: 'amount',
  WEIGHT: 'weight',
  TOTAL_WEIGHT: 'totalWeight',
  ORDER_NO: 'orderName',
  ORDER_REMARK: 'orderRemark',
  CUSTOM_FIELD_3: 'customField3',
  CUSTOM_FIELD_4: 'customField4',
  TYPE: 'type',
  PROBE: 'probe',
  MATERIAL: 'material',
  DRAWING_NO: 'drawingNo',

  // in pt9800 these are used for buttons to select something for related fields
  // for example "PART_SELECTION_LIST" is used to select part and fill "PART_NO"
  PART_SELECTION_LIST: undefined,
  ORDER_SELECTION_LIST: undefined,

  // no direct mapping
  AMOUNT_2: undefined,
  EDIT_IN_WINDOW: undefined,
  START_BASEDATA_PARTS: undefined,
  INTERNAL_ORDER_NO: undefined,
};

const TOKEN_TYPES = Object.values(PT9800TemplateTokenType).join('|');
/*
?: - non-capturing group, we don't need to capture the token type again since it's already captured in the first group
\\. - matches an escaped character (e.g., \, \}, \=)
[^\\}] - matches any character that is not a backslash or closing brace
\\}(?!\\}) - matches a closing brace that is not followed by another closing brace (to avoid matching the end of the template)
*/
const TOKEN_REGEX = new RegExp(
  `\\{\\{(${TOKEN_TYPES}),((?:\\\\.|[^\\\\}]|\\}(?!\\}))+)\\}\\}`,
  'g',
);

/**
 * Splits a string by a delimiter, respecting backslash escape sequences.
 * `\<delimiter>` is treated as a literal character (not a split point).
 * `\\` is treated as a literal backslash.
 */
export function splitEscaped(str: string, delimiter: string): string[] {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '\\' && i + 1 < str.length) {
      current += str[i] + str[i + 1];
      i++;
    } else if (str[i] === delimiter) {
      parts.push(current.trim());
      current = '';
    } else {
      current += str[i];
    }
  }
  parts.push(current.trim());
  return parts;
}

/**
 * Unescapes backslash escape sequences in a template parameter value.
 * - `\\` → `\`
 * - `\,` → `,`
 * - `\}` → `}`
 * - `\=` → `=`
 */
export function unescapeTemplateParam(str: string): string {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    if (str[i] === '\\' && i + 1 < str.length) {
      result += str[i + 1];
      i++;
    } else {
      result += str[i];
    }
  }
  return result;
}

export const PT98_TRANSLATIONS_CACHE = new Map<string, string>();
export const PT98_TEMPLATE_WARNING_CACHE = new Set<string>();

const UNRESOLVED_I18N_PLACEHOLDER_REGEX = /\{[^{}]+\}/;
const PT98_LITERAL_LBRACE = '__PT98_LITERAL_LBRACE__';
const PT98_LITERAL_RBRACE = '__PT98_LITERAL_RBRACE__';

function encodeLiteralBraces(value: string): string {
  return value
    .replaceAll('{', PT98_LITERAL_LBRACE)
    .replaceAll('}', PT98_LITERAL_RBRACE);
}

function decodeLiteralBraces(value: string): string {
  return value
    .replaceAll(PT98_LITERAL_LBRACE, '{')
    .replaceAll(PT98_LITERAL_RBRACE, '}');
}

function notifyPt9800TemplateWarning(message: string, details: Record<string, unknown>): void {
  const warningKey = `${message}|${JSON.stringify(details)}`;
  if (PT98_TEMPLATE_WARNING_CACHE.has(warningKey)) {
    return;
  }

  PT98_TEMPLATE_WARNING_CACHE.add(warningKey);
  Sentry.captureMessage(message, {
    level: 'warning',
    extra: details,
  });
}

function resolveI18nToken(rawValue: string): string {
  const [
    translationKey,
    ...rawParams
  ] = splitEscaped(rawValue, ',').map((part) => part);

  const i18nKey = `api.pt9800.pt9800Translations.${unescapeTemplateParam(translationKey)}`;
  const hasParams = rawParams.length > 0;
  const hasNamedParams = hasParams && rawParams.every((param) => splitEscaped(param, '=').length > 1);

  let paramPayload;
  if (hasParams) {
    if (hasNamedParams) {
      paramPayload = rawParams.reduce((acc, param) => {
        const [
          name,
          ...valueParts
        ] = splitEscaped(param, '=');
        // valueParts.join('=') allows not to escape all = signs in the value, only the first one is used as a delimiter
        acc[unescapeTemplateParam(name)] = encodeLiteralBraces(unescapeTemplateParam(valueParts.join('=')));
        return acc;
      }, {} as Record<string, string>);
    } else {
      paramPayload = rawParams.map((param) => encodeLiteralBraces(unescapeTemplateParam(param)));
    }
  }

  const cacheKey = hasParams
    ? `${i18nKey}|${JSON.stringify(paramPayload)}`
    : i18nKey;

  if (!PT98_TRANSLATIONS_CACHE.has(cacheKey)) {
    let translatedWithSentinels: string;
    // i18n.t can handle both array and object payloads, but we need to make sure to pass the correct type based on the input
    if (Array.isArray(paramPayload)) {
      translatedWithSentinels = i18n.global.t(i18nKey, paramPayload);
    } else if (paramPayload) {
      translatedWithSentinels = i18n.global.t(i18nKey, paramPayload);
    } else {
      translatedWithSentinels = i18n.global.t(i18nKey);
    }

    if (UNRESOLVED_I18N_PLACEHOLDER_REGEX.test(translatedWithSentinels)) {
      notifyPt9800TemplateWarning('PT9800 i18n token resolved with unresolved placeholders', {
        i18nKey,
        rawValue,
        rawParams,
        translated: decodeLiteralBraces(translatedWithSentinels),
      });
    }

    PT98_TRANSLATIONS_CACHE.set(cacheKey, decodeLiteralBraces(translatedWithSentinels));
  }

  return PT98_TRANSLATIONS_CACHE.get(cacheKey)!;
}

/**
 * Resolves PT9800 template placeholders inside a string.
 *
 * Supported placeholder formats:
 *  - {{I18N,key}} → translated text from pt9800Translations
 *  - {{DATE,timestamp}} → formatted date
 *  - {{DATETIME,timestamp}} → formatted date & time
 *  - {{DURATION,timestamp}} → formatted duration (seconds)
 *
 * Notes:
 * - `timestamp` is expected to be a number in seconds.
 * - Multiple placeholders can exist in the same string.
 * - If `template` is `null` or `undefined`, it is returned as-is.
 *
 * @typeParam T - String type or nullish value to preserve input typing
 * @param template - Input string containing PT9800 template placeholders
 * @returns The resolved string with all placeholders replaced, or the original nullish value
 *
 * @example
 * resolvePT9800Template('Started at {{DATETIME,1707408000}}')
 *
 * @example
 * resolvePT9800Template('{{I18N,T_SHOP_PF_STATUS_FINISHED}}')
 */
export function resolvePT9800Template<T extends string | undefined | null>(
  template: T,
): T {
  if (isNullish(template)) {
    return template;
  }

  return template.replace(
    TOKEN_REGEX,
    (_, type: PT9800TemplateTokenType, rawValue: string) => {
      const timestamp = parseInt(rawValue, 10) * 1000;
      let exhaustiveCheck: never;

      switch (type) {
        case PT9800TemplateTokenType.I18N:
          return resolveI18nToken(rawValue);

        case PT9800TemplateTokenType.DATE:
          return formatDayJsDateWithoutTime(dayjs(timestamp), true);

        case PT9800TemplateTokenType.DATETIME:
          return formatDayjsDate(dayjs(timestamp), true);

        case PT9800TemplateTokenType.DURATION:
          return formatFromDuration(dayjs.duration(timestamp));

        default:
          exhaustiveCheck = type;
          throw new Error(`Unhandled token ${exhaustiveCheck}`);
      }
    },
  ) as T;
}

export function deepResolvePT9800Templates<T>(obj: T): T {
  if (typeof obj === 'string') {
    return resolvePT9800Template(obj) as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) =>
      deepResolvePT9800Templates(item),
    ) as T;
  }

  if (isPlainObject(obj)) {
    const result: Record<string, unknown> = {};

    Object.keys(obj).forEach((key) => {
      const value = obj[key];
      result[key] = deepResolvePT9800Templates(value);
    });

    return result as T;
  }

  // return everything that is not a string, array, or object as-is
  return obj;
}

export function resolvePt9800TemplatesInRequest<T extends Promise<AxiosResponse<any>>>(requestPromise: T): T {
  return requestPromise.then((response) => {
    response.data = deepResolvePT9800Templates(response.data);
    return response;
  }).catch((error) => {
    if (error.response?.data?.message) {
      error.response.data.message = resolvePT9800Template(error.response?.data?.message);
    }
    if (error.response?.data?.detail) {
      if (typeof error.response.data.detail === 'string') {
        try {
          error.response.data.detail = deepResolvePT9800Templates(JSON.parse(error.response.data.detail));
        } catch {
          // nothing to do here
        }
      }
    }
    throw error;
  }) as T;
}

export function mapOrderToPt9800Part(order: components['schemas']['order'], position: number): components['schemas']['processFlowPartData'] {
  return {
    partName: order.partName || '',
    partDescription: '',
    amount: order.amountToProduce,
    orderName: order.orderNo,
    orderRemark: '',
    customer: '',
    pos: position,
    customField3: '',
    customField4: '',
    weight: 0,
    totalWeight: 0,
    orderId: order.id,
    partId: order.partId,
    type: '',
  };
}

/**
 * Function which maps Qmulus part of PT9800 part
 * This function returns only fields which can be mapped,
 * so consumer can use easy replace for fields
 */
export function mapQmulusPartToPt9800Part(part: componentsQmulus['schemas']['PartDetails'], position: number): Partial<components['schemas']['processFlowPartData']> {
  return {
    partId: (part.dataSourceReference as Record<string, any>)?.part_id ?? null,
    partName: part.displayName || '',
    partDescription: part.description || '',
    customer: part.customerDisplayName || '',
    pos: position,
    weight: part.attributes
      ?.find((_attribute) => attributeUnitMap[_attribute.partAttributeId as componentsQmulus['schemas']['BuiltInPartAttributeId']] === 'WEIGHT')
      ?.value as number || 0,
  };
}

type TPt9800AnalysisKey = keyof components['schemas']['elementValueList'];

// pt9800 keeps a fixed 13-element `analysis` keyed by the lower-case chemical symbol, while
// Qmulus reports composition with upper-case symbols (ChemicalElementInSteel) over a wider
// element set. Derive the valid keys and their precision from the pt9800 zod schema so this
// stays the single source of truth.
const PT9800_ANALYSIS_KEYS = Object.keys(materialZod.shape.analysis.shape) as TPt9800AnalysisKey[];
const PT9800_ANALYSIS_FRACTION_DIGITS = getZodNumberLimits(materialZod.shape.analysis.shape.c).maxFractionDigits ?? 3;
const PT9800_CARBON_CONTENT_FRACTION_DIGITS = getZodNumberLimits(materialZod.shape.carbonContent).maxFractionDigits ?? 2;

/**
 * pt9800 stores the chosen material as `material.customerMaterialId`, where `0` means "no material selected".
 */
export const NONE_SELECTED_CUSTOMER_MATERIAL_ID = 0;

/**
 * Maps a Qmulus material onto the pt9800 material fields that can be derived from it: the chemical
 * `analysis`, `carbonContent` (taken from carbon), and `customerMaterialId` (the pt9800 customer
 * material id carried in the Qmulus `dataSourceReference`). Every other pt9800 field
 * (alloyFactor, grainSize, targetSurfC, …) has no Qmulus counterpart, so the result is a `Pick` of
 * just those fields, meant to be spread over an existing pt9800 material.
 *
 * Qmulus reports more elements than pt9800 stores (e.g. Fe, W, Co, Nb, Pb, Zr, Ta, N, Se); those
 * have no pt9800 slot and are skipped. Works for every pt9800 endpoint that embeds `material`
 * (recipeItemVersion, recipeOfLoad, beltRecipeItemVersion) since they all share this schema.
 */
export function mapQmulusMaterialToPt9800Material(
  qmulusMaterial: componentsQmulus['schemas']['Material'],
): Pick<components['schemas']['material'], 'analysis' | 'carbonContent' | 'customerMaterialId'> {
  const analysis = Object.fromEntries(
    PT9800_ANALYSIS_KEYS.map((analysisKey) => [
      analysisKey,
      0,
    ]),
  ) as components['schemas']['elementValueList'];

  qmulusMaterial.materialComposition.forEach((elementSpecification) => {
    // pt9800 uses lower-case element symbols, Qmulus upper-case.
    const analysisKey = elementSpecification.elementSymbol.toLowerCase() as TPt9800AnalysisKey;
    if (PT9800_ANALYSIS_KEYS.includes(analysisKey)) {
      analysis[analysisKey] = roundTo(elementSpecification.avgWeightPercentage, PT9800_ANALYSIS_FRACTION_DIGITS);
    }
  });

  const dataSourceReference = qmulusMaterial.dataSourceReference as componentsQmulus['schemas']['CustomerMaterialIdentifier'] | null | undefined;

  return {
    analysis,
    carbonContent: roundTo(analysis.c, PT9800_CARBON_CONTENT_FRACTION_DIGITS),
    // Standard materials and Qmulus-only materials do not exist as pt9800 customer materials.
    customerMaterialId: dataSourceReference?.type === 'CustomerMaterialIdentifier'
      ? dataSourceReference.material_id
      : NONE_SELECTED_CUSTOMER_MATERIAL_ID,
  };
}
